import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { sendOtp, verifyOtp, loginWithPassword } from '../../api/auth';
import { useAuthStore } from '../../store/auth';
import styles from './LoginPage.module.css';

type Tab = 'password' | 'otp';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuthStore();

  const [tab, setTab] = useState<Tab>('password');

  // password login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // otp state
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [session, setSession] = useState('');
  const [otpStep, setOtpStep] = useState<'phone' | 'otp'>('phone');

  const [loading, setLoading] = useState(false);

  const from = (location.state as { from?: string })?.from || '/';

  function applyToken(data: { accessToken?: string; idToken?: string; refreshToken?: string }, phoneNum: string) {
    const token = data.idToken || data.accessToken || '';
    const groups: string[] = [];
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      groups.push(...(payload['cognito:groups'] || []));
    } catch {}
    setAuth(token, phoneNum, groups.includes('ADMINS'));
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    toast.success('Welcome to Sekar Silks!');
    navigate(from, { replace: true });
  }

  const handlePasswordLogin = async () => {
    if (!username || !password) return toast.error('Enter username and password');
    setLoading(true);
    try {
      const data = await loginWithPassword(username, password);
      applyToken(data, username);
    } catch {
      toast.error('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (phone.length < 10) return toast.error('Enter a valid 10-digit number');
    setLoading(true);
    try {
      const data = await sendOtp(phone);
      setSession(data.session || '');
      setOtpStep('otp');
      toast.success(t('auth.otp_sent'));
    } catch {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 6) return toast.error('Enter the 6-digit OTP');
    setLoading(true);
    try {
      const data = await verifyOtp(phone, otp, session);
      applyToken(data, `+91${phone}`);
    } catch {
      toast.error('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>Sekar Silks</div>
        <h1 className={styles.title}>{t('auth.login_title')}</h1>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${tab === 'password' ? styles.tabActive : ''}`}
            onClick={() => setTab('password')}
          >
            Password
          </button>
          <button
            className={`${styles.tab} ${tab === 'otp' ? styles.tabActive : ''}`}
            onClick={() => setTab('otp')}
          >
            OTP
          </button>
        </div>

        {tab === 'password' ? (
          <div className={styles.form}>
            <label className={styles.label}>Username / Phone</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="+917708540067"
              className={styles.input}
              onKeyDown={e => e.key === 'Enter' && handlePasswordLogin()}
            />
            <label className={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password"
              className={styles.input}
              onKeyDown={e => e.key === 'Enter' && handlePasswordLogin()}
            />
            <button className={styles.btn} onClick={handlePasswordLogin} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        ) : (
          <div className={styles.form}>
            {otpStep === 'phone' ? (
              <>
                <label className={styles.label}>{t('auth.phone_label')}</label>
                <div className={styles.phoneInput}>
                  <span className={styles.prefix}>+91</span>
                  <input
                    type="tel"
                    maxLength={10}
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                    placeholder={t('auth.phone_placeholder')}
                    className={styles.input}
                    onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                  />
                </div>
                <button className={styles.btn} onClick={handleSendOtp} disabled={loading}>
                  {loading ? t('auth.sending') : t('auth.send_otp')}
                </button>
              </>
            ) : (
              <>
                <p className={styles.sentTo}>OTP sent to +91 {phone}</p>
                <label className={styles.label}>{t('auth.otp_label')}</label>
                <input
                  type="tel"
                  maxLength={6}
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder={t('auth.otp_placeholder')}
                  className={`${styles.input} ${styles.otpInput}`}
                  onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
                  autoFocus
                />
                <button className={styles.btn} onClick={handleVerifyOtp} disabled={loading}>
                  {loading ? t('auth.verifying') : t('auth.verify')}
                </button>
                <button className={styles.linkBtn} onClick={() => setOtpStep('phone')}>
                  ← Change number
                </button>
                <button className={styles.linkBtn} onClick={handleSendOtp}>
                  {t('auth.resend')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
